import { useEffect, useState, useCallback } from "react";
import { useAiInsightsGate } from "../../../ai_insights";
import { SessionStatusBadges } from "../../../session";
import { usePatientGroups, groupLabel } from "../../../patient";
import { useSessionPreferences } from "../../../center";
import { deleteSession, listCoupleSessions, listGroupSessions, listPatientSessions } from "../../application/sessions";
import type { Session } from "../../../session";
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Pencil, Trash2, ArrowUp, ArrowDown, Calendar, Video, User, Users, Plus, History } from "lucide-react";
import { GroupOfPeopleIcon } from "../../../../shared/ui/icons/SubjectKindIcon";
import { Pagination } from "../../../../shared/ui/common/Pagination";
import flatpickr from "flatpickr";
import Label from "../../../../shared/ui/form/Label";
import DatePicker from "../../../../shared/ui/form/date-picker";
import Button from "../../../../shared/ui/button/Button";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import RecordPastSessionsImport from "./RecordPastSessionsImport";

import { formatDate } from "../../../../shared/utils/date";

interface PatientViewSessionsProps {
  patientID: string;
  isCouple?: boolean;
  /** `patientID` is a group's id: list the sessions held with the group. */
  isGroup?: boolean;
  pageSize?: number;
  hideHeader?: boolean;
  simple?: boolean;
}

export default function PatientViewSessions({
  patientID,
  isCouple = false,
  isGroup = false,
  pageSize = 5,
  hideHeader = false,
  simple = false,
}: PatientViewSessionsProps) {
  const { t, i18n } = useTranslation();
  // The analysis status is the AI tool's: shown only to those who use it.
  const { adopted: showAnalysis } = useAiInsightsGate();
  const { isMobile } = useBreakpoint();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 🔹 Paginación
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  // 🔹 Sorting
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // An individual patient in groups has two histories: their own sessions, and
  // the group sessions they took part in. A switch picks which one is listed.
  const { modes } = useSessionPreferences();
  const showScopes = !isGroup && !isCouple && !simple;
  const { groups } = usePatientGroups(patientID, showScopes && modes.group);
  const [scope, setScope] = useState<"individual" | "group">("individual");
  const [groupId, setGroupId] = useState<string | null>(null);
  const activeGroupId =
    scope === "group" ? (groups.find((group) => group.id === groupId) ?? groups[0])?.id ?? null : null;

  const navigate = useNavigate();
  // Sessions held before Alchely, brought in so the reports count them.
  const [importingHistory, setImportingHistory] = useState(false);


  // 🔹 Filtros de sesión
  const [filters, setFilters] = useState<{ fromDate: string; toDate: string }>({
    fromDate: "",
    toDate: "",
  });

  // 🔹 Key para forzar re-render de DatePickers
  const [dateKey, setDateKey] = useState(0);


  // 🔹 Handlers de fechas
  const handleFromDateChange = (selectedDates: Date[]) => {
    const formattedDate = selectedDates[0]
      ? flatpickr.formatDate(selectedDates[0], "Y-m-d")
      : "";
    setFilters((prev) => ({ ...prev, fromDate: formattedDate }));
  };

  const handleToDateChange = (selectedDates: Date[]) => {
    const formattedDate = selectedDates[0]
      ? flatpickr.formatDate(selectedDates[0], "Y-m-d")
      : "";
    setFilters((prev) => ({ ...prev, toDate: formattedDate }));
  };

  // 🔹 Clear: reset filtros y forzar re-render
  const handleClearFilters = () => {
    setFilters({ fromDate: "", toDate: "" });
    setDateKey((prev) => prev + 1);
  };

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);

      const params: Record<string, unknown> = {
        offset,
        limit: pageSize,
      };

      if (filters?.fromDate) params.from_date = filters.fromDate;
      if (filters?.toDate) params.to_date = filters.toDate;

      const res = activeGroupId
        ? await listGroupSessions(activeGroupId, params)
        : isGroup
        ? await listGroupSessions(patientID, params)
        : isCouple
          ? await listCoupleSessions(patientID, params)
          : await listPatientSessions(patientID, params);
      const data = (res.sessions || res).sort((a: Session, b: Session) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      });
      setSessions(data);
      setTotal(res.total || data.length);
    } catch (err) {
      console.error("Error fetching sessions:", err);
      setError(t("session.errorLoading"));
    } finally {
      setLoading(false);
    }
  }, [patientID, pageSize, offset, filters, t, sortOrder, isCouple, isGroup, activeGroupId]);

  const toggleSort = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // 🗑️ Eliminar sesión
  const handleDelete = async (id: string) => {
    if (!confirm(t("session.deleteConfirm"))) return;
    try {
      await deleteSession(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error("Error deleting session:", err);
      alert(t("session.deleteError"));
    }
  };

  // ✏️ Editar sesión
  const handleEdit = (session: Session) => {
    navigate(`/session/${session.id}`);
  };

  // 🔹 Controles de paginación
  const handlePrevPage = () => setOffset((prev) => Math.max(prev - pageSize, 0));
  const handleNextPage = () => setOffset((prev) => prev + pageSize);



  if (error)
    return <p className="text-center text-red-500 py-10">{error}</p>;

  return (
    <div className={hideHeader ? "" : "glass-effect glass-border border rounded-3xl p-8 shadow-theme-md"}>
      {!hideHeader && (
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
          <h3 className="text-xl font-bold text-content-primary flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-white/5 text-gray-500 flex items-center justify-center">
              <Calendar className="w-5 h-5" />
            </div>
            {t("patient.sessions")}
          </h3>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setImportingHistory(true)}
              className="w-full sm:w-auto"
              size={isMobile ? "sm" : "md"}
              startIcon={<History className="w-4 h-4" />}
            >
              <span className={isMobile ? "text-xs" : ""}>
                {t("pastSessions.addPast")}
              </span>
            </Button>
            <Button
              onClick={() =>
                navigate(
                  activeGroupId
                    ? `/sessions/new?group=${activeGroupId}`
                    : `/sessions/new?${isGroup ? "group" : isCouple ? "couple" : "patient"}=${patientID}`
                )
              }
              className="w-full sm:w-auto"
              size={isMobile ? "sm" : "md"}
              startIcon={<Plus className="w-4 h-4" />}
            >
              <span className={isMobile ? "text-xs" : ""}>
                {t("session.newSession")}
              </span>
            </Button>
          </div>
        </div>
      )}
      <div className="space-y-6">
        {showScopes && groups.length > 0 && (
          <div className="flex flex-col gap-3">
            <div role="tablist" aria-label={t("patient.sessions")} className="flex w-full max-w-sm rounded-xl bg-gray-100 p-1 dark:bg-white/5">
              {(["individual", "group"] as const).map((kind) => (
                <button
                  key={kind}
                  type="button"
                  role="tab"
                  aria-selected={scope === kind}
                  onClick={() => {
                    setScope(kind);
                    setOffset(0);
                  }}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all ${scope === kind ? "bg-white text-accent-text shadow-sm dark:bg-white/10" : "text-gray-500 hover:text-gray-900 dark:hover:text-white"}`}
                >
                  {kind === "group" ? <GroupOfPeopleIcon className="h-4 w-4" /> : <User className="h-4 w-4" />}
                  {t(`session.scope.${kind}`)}
                </button>
              ))}
            </div>
            {scope === "group" && groups.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {groups.map((group) => (
                  <button
                    key={group.id}
                    type="button"
                    aria-pressed={group.id === activeGroupId}
                    onClick={() => {
                      setGroupId(group.id);
                      setOffset(0);
                    }}
                    className={`rounded-full border px-3 py-1 text-sm font-medium transition-colors ${group.id === activeGroupId ? "border-brand-500 bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-300" : "border-gray-200 text-content-secondary hover:border-brand-300 dark:border-gray-800"}`}
                  >
                    {groupLabel(group)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 🔹 Filtros de sesión */}
        {!simple && (
          <div className="flex flex-col sm:flex-row gap-4 items-end bg-gray-50/50 dark:bg-white/[0.02] p-4 rounded-2xl border border-gray-100 dark:border-white/[0.05]">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 w-full">
              <div className="space-y-1.5">
                <Label htmlFor="from-date" className="text-xs font-bold text-content-tertiary uppercase tracking-wider">{t("session.filterFromDate")}</Label>
                <DatePicker
                  key={`from-${dateKey}`}
                  id="from-date"
                  label=""
                  placeholder={t("session.selectStartDate")}
                  defaultDate={filters.fromDate}
                  onChange={handleFromDateChange}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="to-date" className="text-xs font-bold text-content-tertiary uppercase tracking-wider">{t("session.filterToDate")}</Label>
                <DatePicker
                  key={`to-${dateKey}`}
                  id="to-date"
                  label=""
                  placeholder={t("session.selectEndDate")}
                  defaultDate={filters.toDate}
                  onChange={handleToDateChange}
                />
              </div>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button 
                variant="outline" 
                onClick={handleClearFilters}
                className="flex-1 sm:flex-none h-[42px]"
                size="sm"
              >
                {t("common.clear")}
              </Button>
              <button
                onClick={toggleSort}
                className="inline-flex items-center justify-center gap-2 px-3 h-[42px] text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 dark:bg-gray-900 dark:border-gray-800 dark:text-gray-300 dark:hover:bg-gray-800 shadow-sm transition-all min-w-[42px]"
              >
                {sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 relative">
          {/* Layout loading overlay */}
          {loading && sessions.length > 0 && (
            <div className="absolute inset-0 bg-white/50 z-10 dark:bg-gray-900/50 flex justify-center items-center rounded-2xl">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
            </div>
          )}

          {loading && sessions.length === 0 ? (
            // SKELETON LOADER (Card Style)
            Array.from({ length: pageSize }).map((_, i) => (
              <div key={i} className="p-4 bg-gray-50 dark:bg-white/[0.03] rounded-2xl animate-pulse flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-800"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-1/3"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-1/4"></div>
                </div>
              </div>
            ))
          ) : sessions.length > 0 ? (
            sessions.map((session) => {
              // The session's name heads the row; the therapy goes below it.
              const title = session.title?.trim() || null;
              const therapy = (session.therapy_type ?? "")
                .split(", ")
                .filter(Boolean)
                .map((tt) => t(`session.therapyOptions.${tt}`, tt))
                .join(", ");
              const headline = title ?? (therapy || t("session.untitled"));
              return (
              <div
                key={session.id}
                className="group relative flex flex-col sm:flex-row items-center justify-between p-4 bg-white border border-gray-200 rounded-2xl shadow-theme-xs hover:shadow-theme-md transition-all duration-200 dark:bg-gray-900 dark:border-gray-800"
              >
                <div className="flex w-full min-w-0 flex-1 items-center gap-4">
                  {/* Avatar (representing the session format) */}
                  <div
                    className={`flex items-center justify-center w-12 h-12 rounded-full shrink-0 ${session.ubication === "online"
                      ? "bg-accent-tint text-accent-text"
                      : session.type === "couple"
                        ? "bg-brand-100 text-brand-600 dark:bg-brand-900/40 dark:text-brand-400"
                        : "bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-400"
                      }`}
                  >
                    {session.ubication === "online" ? (
                      <Video className="w-5 h-5 text-current opacity-80" />
                    ) : session.type === "group" ? (
                      <GroupOfPeopleIcon className="w-5 h-5 text-current opacity-80" />
                    ) : session.type === "couple" ? (
                      <Users className="w-5 h-5 text-current opacity-80" />
                    ) : (
                      <User className="w-5 h-5 text-current opacity-80" />
                    )}
                  </div>

                  {/* Two lines: what it is, then when and how it went. The
                      patient is the page's, and individual is the default, so
                      neither is repeated on every row. */}
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <Link
                      to={`/session/${session.id}`}
                      className="block min-w-0 group-hover:text-brand-500 transition-colors"
                    >
                      <h3 className="truncate text-base font-semibold text-content-primary">
                        {headline}
                      </h3>
                    </Link>
                    <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-content-tertiary">
                      <span className="flex shrink-0 items-center gap-1.5 font-medium">
                        <Calendar className="w-3.5 h-3.5" aria-hidden="true" />
                        {session.date ? formatDate(session.date, i18n.language) : "—"}
                      </span>
                      {(session.type === "couple" || session.type === "group") && (
                        <span className="shrink-0 px-2 py-0.5 text-xs font-medium rounded-full bg-brand-50 text-brand-600 border border-brand-100 dark:bg-brand-500/10 dark:text-brand-400 dark:border-brand-500/20">
                          {session.type === "group" ? t("common.group") : t("common.couple")}
                        </span>
                      )}
                      {title && therapy && (
                        <span className="min-w-0 max-w-full truncate italic">{therapy}</span>
                      )}
                      {(session.course_internal_name || session.course_module_name) && (
                        <span
                          className="min-w-0 max-w-full truncate rounded-full border border-gray-200 px-2 py-0.5 text-xs font-medium text-content-secondary dark:border-gray-700"
                          title={t("session.courseModule")}
                        >
                          {[session.course_internal_name, session.course_module_name].filter(Boolean).join(" · ")}
                        </span>
                      )}
                      <SessionStatusBadges session={session} showAnalysis={showAnalysis} />
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex shrink-0 items-center gap-2 mt-4 sm:mt-0 ml-auto sm:ml-4">
                  <button
                    onClick={() => handleEdit(session)}
                    className="inline-flex items-center justify-center p-2.5 rounded-xl bg-gray-50 text-gray-500 hover:bg-brand-50 hover:text-brand-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-brand-500/10 dark:hover:text-brand-400 transition-all"
                    title={t("common.edit")}
                  >
                    <Pencil className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(session.id)}
                    className="inline-flex items-center justify-center p-2.5 rounded-xl bg-gray-50 text-gray-500 hover:bg-red-50 hover:text-red-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-red-500/10 dark:hover:text-red-400 transition-all"
                    title={t("common.delete")}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
              );
            })
          ) : (
            <div className="p-10 text-center bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
              <p className="text-content-tertiary">{t("session.notFound")}</p>
            </div>
          )}
        </div>

        {/* 🔹 Footer de paginación */}
        {!simple && (
          <>
            <Pagination
              offset={offset}
              total={total}
              pageSize={pageSize}
              onPrev={handlePrevPage}
              onNext={handleNextPage}
            />

          </>
        )}
      </div>
      {importingHistory && (
        <RecordPastSessionsImport
          kind={isGroup ? "group" : isCouple ? "couple" : "patient"}
          id={patientID}
          onClose={() => setImportingHistory(false)}
          onImported={() => void fetchSessions()}
        />
      )}
    </div>
  );
}
